import { type ReactNode } from "react";
import { cx, formatPhone } from "shared/utils";
import { Show } from "shared/components";

export interface MessageAddress {
  name: string;
  address: string;
}

export type MessageAddressVariant =
  | "default"
  // Pop-up floating card (2152:20753): 11px, name stacked over the number.
  | "compact-stacked"
  // Pop-up drawer: 11px single "name • number" line, truncated.
  | "compact-inline";

interface MessageAddressFieldProps {
  label: string;
  address: MessageAddress;
  variant?: MessageAddressVariant;
  // Rendered right after the number — used by the voice card for the redial
  // affordance the design puts on the To row.
  trailing?: ReactNode;
}

/**
 * The "From: / To:" header row shared by the SMS bubble and the voice call card.
 * Renders the display name (when known) and the number, separated by a dot.
 */
export const MessageAddressField = ({
  label,
  address,
  variant = "default",
  trailing
}: MessageAddressFieldProps) => {
  if (variant === "compact-stacked")
    return (
      <div className="flex items-start gap-1">
        <span className="w-8 shrink-0 font-medium text-content-100">
          {label}
        </span>
        <span className="flex min-w-0 flex-col font-medium text-content-300">
          <Show if={!!address.name}>
            <span className="truncate">{address.name}</span>
          </Show>
          <span className="flex min-w-0 items-center gap-1">
            <span className="truncate">{formatPhone(address.address)}</span>
            {trailing}
          </span>
        </span>
      </div>
    );

  return (
    <div
      className={cx(
        "flex items-center",
        variant === "compact-inline" ? "gap-1" : "gap-2"
      )}
    >
      <span
        className={cx(
          "font-medium text-content-100",
          variant === "compact-inline" ? "w-8 shrink-0" : "w-10"
        )}
      >
        {label}
      </span>
      <Show if={!!address.name}>
        <span
          className={cx(
            "font-medium text-content-300",
            variant === "compact-inline" ? "max-w-[300px] truncate" : ""
          )}
        >
          {address.name}
        </span>
        <div
          className={cx(
            "rounded-full bg-content-100",
            variant === "compact-inline" ? "size-0.5 shrink-0" : "size-1"
          )}
        />
      </Show>
      <span
        className={cx(
          "text-content-300",
          variant === "compact-inline" ? "max-w-[300px] truncate" : ""
        )}
      >
        {formatPhone(address.address)}
      </span>
      {trailing}
    </div>
  );
};
